// Mise en lien des noms d'enregistrements dans les réponses de l'assistant IA.
//
// L'hôte annonce, au fil des appels d'outils, les enregistrements nommés qu'il a lus
// (événement SSE "entities" : classe + id + nom). Le LLM se contente d'écrire le nom en
// texte simple ; la transformation en lien cliquable est faite ici, de façon déterministe.
// Extrait de SectionAssistant.vue pour être exécutable et vérifiable hors composant.

/** Un enregistrement nommé, prêt pour la recherche dans le texte (nom déjà replié). */
export interface EntityEntry {
  folded: string;
  cls: string;
  id: number;
}

/** Syntaxe de lien interne explicite, si un modèle la produit de lui-même. */
export const FICHE_LINK_RE = /\[([^\]]+)\]\(ci:([A-Za-z]+)\/(\d{1,12})\)/g;

// Repliement des accents CARACTÈRE POUR CARACTÈRE : la chaîne repliée garde exactement la
// même longueur que l'originale, donc les positions des correspondances y sont valides et
// l'on peut découper le texte d'origine. Les ligatures (œ, æ) sont volontairement laissées
// telles quelles : les replier changerait la longueur et décalerait les offsets.
const ACCENT_FROM = 'àáâãäåçèéêëìíîïñòóôõöùúûüýÿÀÁÂÃÄÅÇÈÉÊËÌÍÎÏÑÒÓÔÕÖÙÚÛÜÝ';
const ACCENT_TO = 'aaaaaaceeeeiiiinooooouuuuyyAAAAAACEEEEIIIINOOOOOUUUUY';

export function fold (s: string): string {
  let out = '';
  for (const ch of s) {
    const i = ACCENT_FROM.indexOf(ch);
    out += i === -1 ? ch : ACCENT_TO[i];
  }
  return out.toLowerCase();
}

function escapeRegExp (s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Ancre inerte : la navigation est faite par le composant, au clic (délégation). */
export function ficheAnchor (cls: string, id: number, label: string): string {
  return `<a href="#" class="assistant-fiche-link" data-ci-class="${cls}" data-ci-id="${id}">${label}</a>`;
}

/** Remplace, dans un segment de texte SANS balise, les noms connus par des liens de fiche. */
function replaceNames (text: string, entries: EntityEntry[]): string {
  // Alternance des noms, les plus longs d'abord : "Benjamin Millepied" gagne sur "Millepied".
  const pattern = entries.map((e) => escapeRegExp(e.folded)).join('|');
  // Bornes de mot tolérantes aux lettres accentuées (\b ne l'est pas).
  const re = new RegExp(`(?<![\\p{L}\\d])(?:${pattern})(?![\\p{L}\\d])`, 'giu');
  const folded = fold(text);
  let out = '';
  let last = 0;
  for (const m of folded.matchAll(re)) {
    const start = m.index ?? 0;
    if (start < last) {
      continue; // chevauchement : le nom le plus long a déjà été posé
    }
    const entry = entries.find((e) => e.folded === m[0].toLowerCase());
    if (!entry) {
      continue;
    }
    out += text.slice(last, start);
    out += ficheAnchor(entry.cls, entry.id, text.slice(start, start + m[0].length));
    last = start + m[0].length;
  }
  return out + text.slice(last);
}

/**
 * Pose les liens de fiche sur du HTML déjà produit : seuls les segments de texte sont
 * traités, jamais l'intérieur d'une ancre existante (pas de lien imbriqué) ni les balises.
 */
export function linkifyNames (html: string, entries: EntityEntry[]): string {
  if (entries.length === 0) {
    return html;
  }
  const parts = html.split(/(<[^>]*>)/);
  let inAnchor = false;
  for (let i = 0; i < parts.length; i++) {
    const p = parts[i];
    if (p.startsWith('<')) {
      if (/^<a\b/i.test(p)) {
        inAnchor = true;
      } else if (/^<\/a\s*>/i.test(p)) {
        inAnchor = false;
      }
      continue;
    }
    if (inAnchor || p.trim() === '') {
      continue;
    }
    parts[i] = replaceNames(p, entries);
  }
  return parts.join('');
}

// ---------------------------------------------------------------------------------------------
// Identifiants internes : ne jamais les montrer à l'utilisateur.
//
// Les « id » numériques et les champs « xxxId » ne servent qu'à enchaîner les appels d'outils.
// La consigne en est donnée au modèle (SystemPrompt.cs), mais un modèle l'oublie : ce filtre
// garantit le résultat à l'affichage, sans dépendre de sa docilité. Il ne s'applique qu'à ce
// qui est vu par l'utilisateur (affichage, copie, export) : le texte brut reste intact dans
// l'historique renvoyé au modèle, qui garde ainsi le fil de la conversation.
//
// Portée volontairement limitée aux formes ÉTIQUETÉES : « ID : 849 », « (id 849) »,
// « projetId : 849 », et les COLONNES de tableau dont l'en-tête est un identifiant. Un nombre
// nu dans une phrase ne se distingue pas d'un montant ou d'un code de statut, et on ne devine
// pas.
// ---------------------------------------------------------------------------------------------

// Étiquette d'un identifiant interne. Casse explicite (pas d'indicateur « i ») : sinon « valid »
// ou « rapid » seraient pris pour un « id ». Le garde-fou de lettre à gauche évite le même piège.
const ID_LABEL = '(?<![A-Za-z])(?:[a-z]+Id|IDs?|Ids?|ids?|[Ii]dentifiants?(?:\\s+internes?)?)';
// Une ou plusieurs valeurs séparées par des virgules (« IDs : 520, 692, 619 »). Pas de « et » :
// « ID 849 et 12 contrats » perdrait le nombre de contrats.
const ID_VALUE = '\\d{1,12}(?:[ \\t]*[,;][ \\t]*\\d{1,12})*(?!\\d)';

// Ligne entière qui ne contient que l'identifiant (puce de liste ou champ isolé).
const ID_WHOLE_LINE = new RegExp(
  `^[ \\t]*(?:[-*•][ \\t]+)?\\**${ID_LABEL}\\**[ \\t]*[:=][ \\t]*\\**${ID_VALUE}\\**[ \\t]*(?:\\n|$)`, 'gm');
// « (ID : 849) », « (id 849) », « [ID: 849] ».
const ID_BRACKETED = new RegExp(`[ \\t]*[(\\[][ \\t]*${ID_LABEL}[ \\t]*[:=]?[ \\t]*${ID_VALUE}[ \\t]*[)\\]]`, 'g');
// « , ID : 849 », « – id : 849 » : étiquette introduite par une virgule ou un tiret.
const ID_AFTER_SEPARATOR = new RegExp(`[ \\t]*[,;–—-][ \\t]*${ID_LABEL}[ \\t]*[:=][ \\t]*${ID_VALUE}`, 'g');
// « ID : 849 » au milieu du texte.
const ID_INLINE = new RegExp(`[ \\t]*${ID_LABEL}[ \\t]*[:=][ \\t]*${ID_VALUE}`, 'g');
// Début de parenthèse d'identifiant en bout de texte : réponse encore en cours d'écriture, la
// parenthèse fermante n'est pas arrivée. On la masque pour ne pas la voir clignoter.
const ID_PENDING = new RegExp(`[ \\t]*[(\\[][ \\t]*${ID_LABEL}[ \\t]*[:=]?[ \\t]*[\\d,; \\t]*$`);

// En-tête de colonne qui est un identifiant (« ID », « Id », « Identifiant », « projetId »).
// Ancré : « ID contrat » ou « Identité » ne sont pas des identifiants internes.
const ID_COLUMN_HEADER = /^\**(?:[a-z]+Id|ID|Id|id|[Ii]dentifiants?(?:\s+internes?)?)\**$/;
// Ligne de séparation d'un tableau markdown, y compris écrite à moitié (réponse en cours) : sans
// cela l'en-tête « ID » resterait visible jusqu'à l'arrivée de la ligne de séparation.
const TABLE_SEPARATOR_LIKE = /^\s*\|[\s|:-]*$/;

function splitTableRow (line: string): { cells: string[]; lead: boolean; trail: boolean } {
  const t = line.trim();
  const lead = t.startsWith('|');
  const trail = t.length > 1 && t.endsWith('|');
  let body = t;
  if (lead) {
    body = body.slice(1);
  }
  if (trail) {
    body = body.slice(0, -1);
  }
  return { cells: body.split('|'), lead, trail };
}

function dropTableCells (line: string, drop: number[]): string {
  const { cells, lead, trail } = splitTableRow(line);
  const kept = cells.filter((_, k) => !drop.includes(k));
  return (lead ? '|' : '') + kept.join('|') + (trail ? '|' : '');
}

// Retire des tableaux markdown les colonnes dont l'en-tête est un identifiant. La cellule est
// retirée par RANG dans chaque ligne, y compris une ligne encore en cours d'écriture : une
// cellule d'identifiant à moitié tapée disparaît donc aussi. Un tableau qui ne serait fait que
// d'identifiants disparaît entièrement.
function stripIdColumns (text: string): string {
  const lines = text.split('\n');
  const out: string[] = [];
  let i = 0;
  while (i < lines.length) {
    const startsTable = /^\s*\|/.test(lines[i])
      && (i + 1 >= lines.length || TABLE_SEPARATOR_LIKE.test(lines[i + 1]));
    if (!startsTable) {
      out.push(lines[i]);
      i++;
      continue;
    }
    let j = i;
    while (j < lines.length && /^\s*\|/.test(lines[j])) {
      j++;
    }
    const block = lines.slice(i, j);
    const header = splitTableRow(block[0]).cells;
    const drop = header.map((c, k) => (ID_COLUMN_HEADER.test(c.trim()) ? k : -1)).filter((k) => k >= 0);
    if (drop.length === 0) {
      out.push(...block);
    } else if (drop.length < header.length) {
      out.push(...block.map((l) => dropTableCells(l, drop)));
    }
    i = j;
  }
  return out.join('\n');
}

/**
 * Retire d'un texte de réponse les identifiants internes étiquetés et les colonnes d'identifiants
 * des tableaux. Idempotent, et renvoie le texte inchangé (au caractère près) quand il n'y en a
 * pas. Chaque règle emporte l'espace qui la précède : aucun recollage global, qui altérerait la
 * typographie (espace avant « : »).
 */
export function stripInternalIds (text: string): string {
  let t = stripIdColumns(text).replace(ID_WHOLE_LINE, '');
  t = t.replace(ID_BRACKETED, '');
  // Avant les règles nues : « (ID : 84 » en bout de texte doit partir en entier, parenthèse
  // comprise, et non laisser « ( » orpheline une fois « ID : 84 » retiré.
  t = t.replace(ID_PENDING, '');
  t = t.replace(ID_AFTER_SEPARATOR, '');
  return t.replace(ID_INLINE, '');
}

/**
 * Vrai si la question de l'utilisateur demande explicitement un identifiant : la réponse doit
 * alors le donner, et le filtre ne s'applique pas.
 */
export function asksForInternalId (question: string): boolean {
  return /(?<![A-Za-z])(?:ids?|identifiants?)(?![A-Za-z])/i.test(question);
}

// ---------------------------------------------------------------------------------------------
// Paragraphes répétés. Avec des outils, le modèle écrit une phrase de conclusion, rappelle un outil,
// puis la réécrit : « Résultat : Les 7 clients… » suivi, un paragraphe plus bas, de « Les 7 clients… »
// seule. Comme pour les identifiants, on ne s'en remet pas à sa docilité : l'affichage retire la
// répétition. Le texte brut reste intact dans l'historique renvoyé au modèle.
// ---------------------------------------------------------------------------------------------

// En deçà, une ligne identique est plus probablement légitime (« Oui. », un intitulé) qu'une répétition.
const MIN_REPEATED_SUFFIX = 20;
const MIN_PENDING_PREFIX = 12;

function normalisedParagraph (p: string): string {
  return p.replace(/\s+/g, ' ').trim();
}

/**
 * Retire les paragraphes qui répètent le PRÉCÉDENT : à l'identique, ou en en reprenant la fin (« Résultat :
 * <phrase> » puis « <phrase> »). Seules les répétitions CONSÉCUTIVES sont retirées : un même paragraphe
 * qui revient plus loin dans une réponse longue est du contenu. Le dernier paragraphe, encore en cours
 * d'écriture, est masqué tant qu'il n'est que le début du précédent, pour ne pas le voir apparaître puis
 * disparaître. Renvoie le texte inchangé (au caractère près) s'il n'y a rien à retirer.
 */
export function collapseRepeatedParagraphs (text: string): string {
  const paragraphs = text.split(/\n{2,}/);
  if (paragraphs.length < 2) {
    return text;
  }
  const kept: string[] = [];
  let previous = '';
  paragraphs.forEach((paragraph, index) => {
    const key = normalisedParagraph(paragraph);
    if (key.length > 0 && previous.length > 0) {
      const sameParagraph = key === previous && key.length >= MIN_REPEATED_SUFFIX;
      const sameEnding = key.length >= MIN_REPEATED_SUFFIX && previous.endsWith(key);
      const startOfPrevious = index === paragraphs.length - 1
        && key.length >= MIN_PENDING_PREFIX && previous.startsWith(key);
      if (sameParagraph || sameEnding || startOfPrevious) {
        return;
      }
    }
    kept.push(paragraph);
    if (key.length > 0) {
      previous = key;
    }
  });
  return kept.length === paragraphs.length ? text : kept.join('\n\n');
}
